import { useState, useEffect, useRef } from "react";
import { getAnswer } from "../../data/moneybotKB";
import { track } from "../../lib/track";
import { loadPriority, loadTD } from "../../lib/storage";
import { askMoneyBot, MoneyBotError, OFFLINE_MSG, BUSY_MSG, TIMEOUT_MSG, previewDebug, type ApiMode, type ChatMsg } from "../../lib/moneybot";
import ChatMarkdown from "../../components/ChatMarkdown";

type AppPath = "starter" | "builder" | "master" | "pro";

function toApiMode(path: AppPath | undefined): ApiMode {
  if (path === "pro") return "adult";
  if (path === "master") return "teen";
  return "kid";
}

function fmt(n: number): string {
  if (n >= 1000) return `$${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return `$${Math.round(n)}`;
}

function buildProContext(priority: any, td: any): string | null {
  const sections: string[] = [];

  if (priority) {
    const income = (priority.expenses || 0) + (priority.surplus || 0);
    if (income > 0 || priority.surplus > 0) {
      sections.push(`income ${fmt(income)}/mo, exp ${fmt(priority.expenses || 0)}/mo, surplus ${fmt(priority.surplus || 0)}/mo`);
    }
  }

  if (td && td.history && td.history.length > 0) {
    const snap = td.history[td.history.length - 1].snapshot;
    const cats = td.categories;
    const assets: string[] = [];
    const debts: string[] = [];

    Object.entries(cats).forEach(([k, c]: [string, any]) => {
      const val = snap[k] || 0;
      if (c.isAsset) {
        if (val > 0 || c.target > 0) {
          const label = k === "savings" ? "cash" : k === "invest" ? "invest" : k === "emergency" ? "emerg" : k;
          const t = c.target > 0 ? `/goal ${fmt(c.target)}` : "";
          assets.push(`${label} ${fmt(val)}${t}`);
        }
      } else if (val > 0) {
        const label = k === "creditCard" ? "CC" : k === "student" ? "student" : k === "carLoan" ? "car" : "other debt";
        debts.push(`${label} ${fmt(val)}`);
      }
    });

    if (assets.length) sections.push(`assets: ${assets.join(", ")}`);
    if (debts.length) sections.push(`debt: ${debts.join(", ")}`);
  }

  if (priority && priority.catPriority && priority.catPriority.length > 0) {
    const labelMap: Record<string, string> = {
      creditCard: "CC", student: "student loans", carLoan: "car loan",
      savings: "cash savings", invest: "investing", emergency: "emergency fund", other: "other debt",
    };
    const top = priority.catPriority.slice(0, 3).map((k: string) => labelMap[k] || k);
    sections.push(`priority: ${top.join(" > ")}`);
    if (priority.risk) sections.push(`risk: ${priority.risk}`);
  }

  if (!sections.length) return null;
  return `[My finances: ${sections.join(" | ")}]`;
}

const ANALYZE_PROMPT = "I'm not hitting my financial goals. Based on my finances, please: 1) tell me exactly what's holding me back, 2) give me a specific revised monthly budget with exact dollar amounts to cut or shift, 3) tell me the single most impactful thing I should do this month.";

const PRO_CHIPS_WITH_DATA = [
  "Why am I not hitting my goals?",
  "What should I cut first?",
  "How fast can I pay off debt?",
  "Help me hit my savings goal",
  "What should I invest in?",
  "How long until financial independence?",
];

const PRO_CHIPS_NO_DATA = [
  "How do I make a budget?",
  "What's a good savings rate?",
  "How do I pay off debt fast?",
  "What's an index fund?",
  "How do I build an emergency fund?",
  "What's a Roth IRA?",
];

const DEFAULT_CHIPS = [
  "What is money?", "How do I save?", "What's a stock?", "What's compound interest?",
  "What's a credit score?", "How do taxes work?", "What's an index fund?", "Good debt vs bad debt?",
];

interface Props {
  path?: AppPath;
}

export default function ChatScreen({ path }: Props) {
  const dark = path === "master";
  const isPro = path === "pro";

  const [proContext, setProContext] = useState<string | null>(() =>
    isPro ? buildProContext(loadPriority(), loadTD()) : null
  );

  const [msgs, setMsgs] = useState<ChatMsg[]>(() => {
    const ctx = isPro ? buildProContext(loadPriority(), loadTD()) : null;
    const welcome = isPro
      ? ctx
        ? "Hi! I can see your financial plan 📊 Ask me anything and I'll answer based on YOUR actual numbers. Try 'What should I cut first?' or hit Analyze My Budget below."
        : "Hi! I'm MoneyBot 🤖 Complete your My Plan screen and I'll give you personalized advice based on your real income, debt, and goals. Or ask me anything general!"
      : "Hi! I'm MoneyBot 🤖💰 Ask me anything about money — saving, investing, taxes, credit, and more! No question is too silly!";
    return [{ role: "bot", text: welcome }];
  });

  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, loading]);

  // Refresh context when user returns from another screen
  useEffect(() => {
    if (!isPro) return;
    const refresh = () => setProContext(buildProContext(loadPriority(), loadTD()));
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [isPro]);

  const chips = isPro
    ? (proContext ? PRO_CHIPS_WITH_DATA : PRO_CHIPS_NO_DATA)
    : DEFAULT_CHIPS;

  async function send(text?: string) {
    const msg = (text ?? q).trim();
    if (!msg || loading) return;
    track("moneybot_asked");
    const history = msgs;
    setMsgs(m => [...m, { role: "user", text: msg, kind: "chat" }]);
    setQ("");
    setLoading(true);
    try {
      const answer = await askMoneyBot({ question: msg, mode: toApiMode(path), context: isPro ? proContext : null, history });
      setMsgs(m => [...m, { role: "bot", text: answer, kind: "chat" }]);
    } catch (err) {
      const reason = err instanceof MoneyBotError ? err.reason : "failed";
      const text = reason === "busy" ? BUSY_MSG : reason === "timeout" ? TIMEOUT_MSG : (getAnswer(msg) ?? OFFLINE_MSG) + previewDebug(err);
      setMsgs(m => [...m, { role: "bot", text }]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ fontFamily: "Nunito", display: "flex", flexDirection: "column", height: "calc(100vh - 70px)", background: dark ? "#0f172a" : "#f0f4ff" }}>
      {/* Header */}
      <div style={{ background: "linear-gradient(135deg,#6366f1,#8b5cf6)", padding: "14px 16px 12px", textAlign: "center", flexShrink: 0 }}>
        <div style={{ fontSize: 36, animation: "bounce 2s infinite", lineHeight: 1 }}>🤖</div>
        <div style={{ fontSize: 22, fontWeight: 900, color: "#fff", fontFamily: "Fredoka One" }}>MoneyBot</div>
        {isPro ? (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 5, marginTop: 6,
            background: proContext ? "rgba(16,185,129,0.25)" : "rgba(255,255,255,0.12)",
            border: `1px solid ${proContext ? "rgba(16,185,129,0.5)" : "rgba(255,255,255,0.2)"}`,
            borderRadius: 20, padding: "3px 10px",
          }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: proContext ? "#10b981" : "#94a3b8", flexShrink: 0 }} />
            <span style={{ fontSize: 11, fontWeight: 800, color: proContext ? "#6ee7b7" : "#94a3b8" }}>
              {proContext ? "Seeing your plan data" : "No plan data yet"}
            </span>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: "#ffffffaa", fontWeight: 700 }}>Ask me anything about money!</div>
        )}
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "14px", display: "flex", flexDirection: "column", gap: 10 }}>
        {msgs.map((m, i) => (
          <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-end", flexDirection: m.role === "user" ? "row-reverse" : "row", animation: "slideUp 0.3s" }}>
            {m.role === "bot" && <div style={{ fontSize: 26, flexShrink: 0, lineHeight: 1 }}>🤖</div>}
            <div style={{
              maxWidth: "82%",
              background: m.role === "user" ? "linear-gradient(135deg,#6366f1,#8b5cf6)" : dark ? "#1e293b" : "#fff",
              color: m.role === "user" ? "#fff" : dark ? "#e2e8f0" : "#1f2937",
              borderRadius: m.role === "user" ? "20px 20px 4px 20px" : "20px 20px 20px 4px",
              padding: "11px 15px", fontSize: 14, fontWeight: 700, lineHeight: 1.6,
              boxShadow: "0 3px 10px #0001",
              border: m.role === "bot" ? `2px solid ${dark ? "#334155" : "#e5e7eb"}` : "none",
              whiteSpace: m.role === "user" ? "pre-wrap" : "normal",
            }}>
              {m.role === "bot" ? <ChatMarkdown text={m.text} /> : m.text}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{ fontSize: 26, lineHeight: 1 }}>🤖</div>
            <div style={{ background: dark ? "#1e293b" : "#fff", borderRadius: 20, padding: "13px 16px", border: `2px solid ${dark ? "#334155" : "#e5e7eb"}`, display: "flex", gap: 5 }}>
              {[0, 1, 2].map(i => (
                <div key={i} style={{ width: 9, height: 9, borderRadius: "50%", background: "#8b5cf6", animation: `bounce 0.9s ${i * 0.18}s infinite` }} />
              ))}
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Input area */}
      <div style={{ background: dark ? "#1e293b" : "#fff", borderTop: `2px solid ${dark ? "#334155" : "#e5e7eb"}`, padding: "8px 12px", paddingBottom: "calc(8px + env(safe-area-inset-bottom, 0px))", flexShrink: 0 }}>
        {/* Analyze My Budget button — always visible in pro mode */}
        {isPro && (
          <button
            onClick={() => proContext
              ? send(ANALYZE_PROMPT)
              : send("How does MoneyBot's budget analysis work, and what do I need to set up first so you can give me personalized budget advice?")
            }
            disabled={loading}
            style={{
              width: "100%", marginBottom: 8,
              background: proContext
                ? "linear-gradient(135deg,#10b981,#0ea5e9)"
                : "transparent",
              border: proContext ? "none" : "2px solid #334155",
              borderRadius: 14, padding: "11px 16px",
              fontSize: 13, fontWeight: 900,
              color: proContext ? "#fff" : "#64748b",
              cursor: "pointer",
              opacity: loading ? 0.5 : 1, transition: "opacity 0.2s",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
            <span>📊</span>
            {proContext ? "Analyze My Budget & Adjust for Goals" : "Analyze My Budget (add plan data first)"}
          </button>
        )}

        {/* Chips */}
        <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 8, scrollbarWidth: "none" }}>
          {chips.map(c => (
            <button key={c} onClick={() => send(c)} disabled={loading}
              style={{ flexShrink: 0, background: dark ? "#0f172a" : "#f3f4f6", border: `2px solid ${dark ? "#334155" : "#e5e7eb"}`, borderRadius: 20, padding: "6px 13px", fontSize: 12, fontWeight: 800, color: dark ? "#a5b4fc" : "#6366f1", cursor: "pointer", whiteSpace: "nowrap", opacity: loading ? 0.5 : 1 }}>
              {c}
            </button>
          ))}
        </div>

        {/* Text input */}
        <div style={{ display: "flex", gap: 8 }}>
          <input value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={e => e.key === "Enter" && send()}
            placeholder={isPro && proContext ? "Ask about your finances... 💬" : "Ask about money... 💬"}
            style={{ flex: 1, background: dark ? "#0f172a" : "#f9fafb", border: `2px solid ${dark ? "#334155" : "#e5e7eb"}`, borderRadius: 14, padding: "11px 14px", fontSize: 14, fontWeight: 700, outline: "none", fontFamily: "Nunito", color: dark ? "#f1f5f9" : "#1f2937" }} />
          <button onClick={() => send()} disabled={!q.trim() || loading}
            className="btn-bounce"
            style={{ background: "linear-gradient(135deg,#6366f1,#8b5cf6)", border: "none", borderRadius: 14, width: 48, height: 48, color: "#fff", fontSize: 22, cursor: "pointer", opacity: !q.trim() || loading ? 0.4 : 1, transition: "opacity 0.2s" }}>→</button>
        </div>
      </div>
    </div>
  );
}
